import type { GuestSummary, RsvpResponseValue } from '@peles/shared';
import type { GuestListState } from './useGuestListParams';

export interface GuestStatTilesProps {
  summary: GuestSummary | undefined;
  state: GuestListState;
  /** Apply the tile's filter (or clear it, when it is the one applied). */
  onFilter: (patch: Partial<GuestListState>) => void;
}

interface Tile {
  id: string;
  label: string;
  value: string;
  sub?: string;
  /** The filter the tile applies. */
  patch: Partial<GuestListState>;
  active: boolean;
  tone: string;
}

const only = (state: GuestListState, response: RsvpResponseValue) =>
  state.response.length === 1 && state.response[0] === response;

/**
 * The numbers above the guest list, over the whole event (never the page),
 * each a button that filters the list to what it counts — tap "No reply" to
 * see who has not answered, "To seat" to see who is coming without a table.
 */
export function GuestStatTiles({ summary, state, onFilter }: GuestStatTilesProps) {
  if (!summary) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" aria-hidden>
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="h-[74px] rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }
  const attendingParties = summary.byResponse.YES;
  const toSeat = summary.unseatedAttendingParties;
  const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const noFilter =
    state.response.length === 0 && !state.seated && state.side.length === 0 && !state.q;

  const tiles: Tile[] = [
    {
      id: 'invited',
      label: 'Invited',
      value: String(summary.parties),
      sub: plural(summary.people.total, 'person', 'people'),
      patch: { response: [], seated: '' },
      active: noFilter,
      tone: 'text-gray-900',
    },
    {
      id: 'attending',
      label: 'Attending',
      value: String(summary.attendingPeople),
      sub: plural(attendingParties, 'party', 'parties'),
      patch: { response: only(state, 'YES') ? [] : ['YES'], seated: '' },
      active: only(state, 'YES') && !state.seated,
      tone: 'text-green-700',
    },
    {
      id: 'declined',
      label: 'Not attending',
      value: String(summary.byResponse.NO),
      sub: 'parties',
      patch: { response: only(state, 'NO') ? [] : ['NO'], seated: '' },
      active: only(state, 'NO'),
      tone: 'text-red-700',
    },
    {
      id: 'pending',
      label: 'No reply',
      value: String(summary.byResponse.PENDING + summary.byResponse.MAYBE),
      sub: summary.byResponse.MAYBE ? `${summary.byResponse.MAYBE} maybe` : 'parties',
      patch: {
        response:
          state.response.length === 2 &&
          state.response.includes('PENDING') &&
          state.response.includes('MAYBE')
            ? []
            : ['PENDING', 'MAYBE'],
        seated: '',
      },
      active:
        state.response.length === 2 &&
        state.response.includes('PENDING') &&
        state.response.includes('MAYBE'),
      tone: 'text-amber-700',
    },
    {
      id: 'seat',
      label: 'To seat',
      value: String(toSeat),
      sub: `${summary.seatedParties} seated`,
      patch:
        only(state, 'YES') && state.seated === 'no'
          ? { response: [], seated: '' }
          : { response: ['YES'], seated: 'no' },
      active: only(state, 'YES') && state.seated === 'no',
      tone: toSeat > 0 ? 'text-primary-700' : 'text-gray-900',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="guest-stats">
      {tiles.map((tile) => (
        <button
          key={tile.id}
          type="button"
          aria-pressed={tile.active}
          onClick={() => onFilter(tile.patch)}
          data-testid={`guest-stat-${tile.id}`}
          className={`text-left rounded-2xl border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ${
            tile.active
              ? 'border-primary-300 bg-primary-50'
              : 'border-gray-200 bg-white hover:border-primary-200'
          }`}
        >
          <div className="text-xs font-medium text-gray-500">{tile.label}</div>
          <div className={`text-2xl font-bold ${tile.tone}`}>{tile.value}</div>
          {tile.sub && <div className="text-xs text-gray-500">{tile.sub}</div>}
        </button>
      ))}
    </div>
  );
}
